/**
 * Drawing the as-scanned vs designed electrical layer on the floor plan —
 * the marks `compareElectrical` returns (see `inspectedElectrical.js`), one
 * colour per change: unchanged, removed, new.
 *
 * SCREEN SPACE, like the wiring and the callouts: the caller enters it through
 * `beginScreenSpaceOverlay` and hands us world → screen. The glyphs are the
 * electrical drawing's own (`drawElectricalDeviceSymbol` / `drawLightSymbol`),
 * called with a scale of 1 so their pixel sizes are screen pixels, and inked in
 * `ELECTRICAL_CHANGE_COLORS` by each mark's `status`.
 *
 * Read-only: nothing here is selectable, and nothing is returned for a hit test.
 */

import {
  drawElectricalDeviceSymbol,
  drawLightSymbol,
} from './electricalDrawingUtils';
import { ELECTRICAL_CHANGE_COLORS } from './inspectedElectrical';

/** World metres along the wall normal used to measure its on-screen angle. */
const NORMAL_PROBE_M = 0.1;

/**
 * The screen angle of a device's local +x — into the room, off the wall it is
 * mounted on. Measured through `worldToScreen` rather than derived from the
 * plan's rotation, so it cannot disagree with where the point itself is drawn.
 */
const screenAngle = (device, at, worldToScreen) => {
  if (!device.inward) return 0;
  const tip = worldToScreen(
    device.position.x + device.inward.x * NORMAL_PROBE_M,
    device.position.z + device.inward.z * NORMAL_PROBE_M
  );
  return Math.atan2(tip.y - at.y, tip.x - at.x);
};

/**
 * @param {CanvasRenderingContext2D} ctx - already in screen space
 * @param {Array} devices - from `compareElectrical`, scoped to the rooms
 *   being drawn
 * @param {(x:number, z:number) => {x:number, y:number}} worldToScreen
 */
export const drawInspectedElectricalOverlay = (ctx, devices, worldToScreen) => {
  for (const device of devices || []) {
    const at = worldToScreen(device.position.x, device.position.z);
    const angle = screenAngle(device, at, worldToScreen);
    ctx.save();
    ctx.translate(at.x, at.y);
    ctx.rotate(angle);
    ctx.strokeStyle = ELECTRICAL_CHANGE_COLORS[device.status];
    ctx.lineWidth = 1.5;
    if (device.symbol === 'device') {
      drawElectricalDeviceSymbol(ctx, device.type, 1, angle);
    } else {
      drawLightSymbol(ctx, device.symbol, 1, angle, 0);
    }
    ctx.restore();
  }
};
